import api from './api';

const BASE_URL = '/api/v1/study-builder';

// Convert 12-hour time to UTC
const convertToUTC = (time) => {
  // Convert 12-hour time to 24-hour time
  let hours = parseInt(time.hours);
  if (time.period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (time.period === 'AM' && hours === 12) {
    hours = 0;
  }

  // Create a UTC date object for today with the specified time
  const date = new Date();
  date.setUTCHours(hours);
  date.setUTCMinutes(parseInt(time.minutes));
  date.setUTCSeconds(0);
  date.setUTCMilliseconds(0);
  
  return date;
};

const reminderService = {
  // Create a new reminder template
  createReminderTemplate: async (templateData, studyDraftId = null) => {
    try {
      const params = studyDraftId ? { study_draft: studyDraftId } : {};
      
      const response = await api.post(`${BASE_URL}/reminder-templates/`, { 
        ...templateData,
        ...params
      });
      return response.data;
    } catch (error) {
      // Extract detailed error message if available
      const errorMessage = extractErrorMessage(error);
      throw { message: errorMessage };
    }
  },

  // Get reminder templates for a study draft
  getReminderTemplates: async (studyDraftId = null) => {
    try {
      const params = studyDraftId ? { study_draft: studyDraftId } : {};
      const response = await api.get(`${BASE_URL}/reminder-templates/`, { params });
      // Handle paginated response
      return response.data || [];
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch reminder templates' };
    }
  },

  // Preview a reminder template
  previewTemplate: async (templateId, context, studyDraftId = null) => {
    try {
      const params = studyDraftId ? { study_draft: studyDraftId } : {};
      const response = await api.post(`${BASE_URL}/reminder-templates/${templateId}/preview/`, { ...context, ...params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to preview template' };
    }
  },

  // Schedule reminders for a template
  scheduleReminders: async (templateId, studyDraftId, targetDates, contextData) => {
    try {
      // Convert reminder times to UTC
      const utcDates = targetDates.map(date => {
        if (date.hours !== undefined) {
          // If it's a time object (from TimeInput), convert to UTC
          return convertToUTC(date);
        }
        // If it's already a Date object, ensure it's in UTC
        return new Date(date.toISOString());
      });

      const response = await api.post(`${BASE_URL}/scheduled-reminders/schedule-batch/`, {
        template: templateId,
        study_draft: studyDraftId,
        target_dates: utcDates.map(date => date.toISOString()),
        context_data: contextData
      });
      return response.data;
    } catch (error) {
      console.error('Error scheduling reminders:', error.response?.data || error);
      throw error.response?.data || { message: 'Failed to schedule reminders' };
    }
  },

  // Cancel a scheduled reminder
  cancelReminder: async (reminderId, studyDraftId = null) => {
    try {
      const params = studyDraftId ? { study_draft: studyDraftId } : {};
      const response = await api.post(`${BASE_URL}/scheduled-reminders/${reminderId}/cancel/`, params);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to cancel reminder' };
    }
  },

  // Update an existing reminder template
  updateReminderTemplate: async (templateId, templateData) => {
    try {
      const response = await api.put(`${BASE_URL}/reminder-templates/${templateId}/`, templateData);
      return response.data;
    } catch (error) {
      // Extract detailed error message if available
      const errorMessage = extractErrorMessage(error);
      throw { message: errorMessage };
    }
  },

  // Delete a reminder template
  deleteReminderTemplate: async (templateId) => {
    try {
      const response = await api.delete(`${BASE_URL}/reminder-templates/${templateId}/`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete reminder template' };
    }
  },

  // Global Library Methods
  getGlobalReminders: async (channel = null) => {
    try {
      const params = channel ? { channel } : {};
      const response = await api.get(`/api/global-library/reminders`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching global reminders:', error);
      throw error.response?.data || { message: `Failed to fetch global reminders` };
    }
  },

  createGlobalReminder: async (reminderData) => {
    try {
      const transformedData = {
        name: reminderData.name,
        channel: reminderData.channel,
        template_content: reminderData.template_content,
        metadata: reminderData.metadata || {},
        default_schedule: reminderData.default_schedule || [1, 3, 7],
        placeholders: reminderData.placeholders || {
          required: [],
          optional: []
        },
        version: reminderData.version || '1.0.0',
        sponsor_id: localStorage.getItem('selectedSponsor'),
        is_global: true
      };

      const response = await api.post(`/api/global-library/reminders`, transformedData);
      return response.data;
    } catch (error) {
      console.error('Error creating global reminder:', error);
      // Extract detailed error message if available
      const errorMessage = extractErrorMessage(error);
      throw { message: errorMessage };
    }
  },

  updateGlobalReminder: async (reminderId, reminderData) => {
    try {
      const transformedData = {
        name: reminderData.name,
        channel: reminderData.channel,
        template_content: reminderData.template_content,
        metadata: reminderData.metadata || {},
        default_schedule: reminderData.default_schedule || [1, 3, 7],
        placeholders: reminderData.placeholders || {
          required: [],
          optional: []
        },
        version: reminderData.version || '1.0.0',
        sponsor_id: localStorage.getItem('selectedSponsor'),
        is_global: true
      };

      const response = await api.put(`/api/global-library/reminders/${reminderId}`, transformedData);
      return response.data;
    } catch (error) {
      console.error('Error updating global reminder:', error);
      // Extract detailed error message if available
      const errorMessage = extractErrorMessage(error);
      throw { message: errorMessage };
    }
  },

  deleteGlobalReminder: async (reminderId) => {
    try {
      await api.delete(`/api/global-library/reminders/${reminderId}`);
    } catch (error) {
      console.error('Error deleting global reminder:', error);
      throw error.response?.data || { message: 'Failed to delete global reminder' };
    }
  },

  importGlobalReminderToStudy: async (globalReminderId, studyId) => {
    try {
      const response = await api.post('/api/global-library/import', {
        global_reminder_id: globalReminderId,
        study_id: studyId
      });
      return response.data;
    } catch (error) {
      console.error('Error importing global reminder to study:', error);
      throw error.response?.data || { message: 'Failed to import global reminder to study' };
    }
  }
};

// Helper function to extract detailed error messages from the response
const extractErrorMessage = (error) => {
  if (!error || !error.response) {
    return 'An unexpected error occurred';
  }

  const { data } = error.response;
  
  // If data is a string, return it directly
  if (typeof data === 'string') {
    return data;
  }
  
  // For validation errors in non_field_errors
  if (data?.non_field_errors && Array.isArray(data.non_field_errors)) {
    return data.non_field_errors.join(', ');
  }
  
  // For field-specific validation errors (like 'name')
  if (data && typeof data === 'object') {
    // Check for specific field errors
    for (const field of ['name', 'channel', 'template_content']) {
      if (data[field] && Array.isArray(data[field])) {
        return `${field.charAt(0).toUpperCase() + field.slice(1)}: ${data[field].join(', ')}`;
      }
    }
    
    // If there are any other field errors, concatenate them
    const fieldErrors = [];
    for (const [field, errors] of Object.entries(data)) {
      if (Array.isArray(errors)) {
        fieldErrors.push(`${field}: ${errors.join(', ')}`);
      } else if (typeof errors === 'string') {
        fieldErrors.push(`${field}: ${errors}`);
      }
    }
    
    if (fieldErrors.length > 0) {
      return fieldErrors.join('; ');
    }
    
    // If data.message exists, return it
    if (data.message) {
      return data.message;
    }
    
    // If data.detail exists, return it
    if (data.detail) {
      return data.detail;
    }
  }
  
  // Fallback
  return 'Failed to process the request. Please try again.';
};

export default reminderService;
